import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import PermissionManagerService from '../services/FileManager/permissionManagement.service';
import { PermissionPrincipal } from '../shared/components/PermissionsPicker';

// staleTime 0 (against the app-wide 30s default): users/groups are added in User Manager, so every
// open must refetch rather than show a roster that is missing them.
const PRINCIPALS_STALE_TIME = 0;

interface RawPrincipal {
  id?: string | number;
  name?: string;
  username?: string;
}

const isRawPrincipal = (value: unknown): value is RawPrincipal => typeof value === 'object' && value !== null;

// Both endpoints are untyped, so neither the array nor the entry shape is
// trusted. A non-array response yields an empty list rather than throwing.
const toPrincipals = (data: unknown): PermissionPrincipal[] => {
  if (!Array.isArray(data)) return [];
  return data.filter(isRawPrincipal).reduce<PermissionPrincipal[]>((principals, entry) => {
    if (entry.id === undefined || entry.id === null) return principals;
    const id = String(entry.id);
    principals.push({ id, name: entry.name ?? entry.username ?? id });
    return principals;
  }, []);
};

// Split out so a caller that only assigns groups doesn't also fetch the user
// roster it never reads.
export const usePermissionGroups = (enabled: boolean = true) => {
  const { getAllGroups } = PermissionManagerService();

  const groupsQuery = useQuery({ ...getAllGroups(), enabled, staleTime: PRINCIPALS_STALE_TIME });
  const groups = useMemo(() => toPrincipals(groupsQuery.data), [groupsQuery.data]);

  return { groups, isLoadingGroups: groupsQuery.isLoading, isGroupsError: groupsQuery.isError };
};

export const usePermissionPrincipals = (enabled: boolean = true) => {
  const { getAllUsers } = PermissionManagerService();

  const usersQuery = useQuery({ ...getAllUsers(), enabled, staleTime: PRINCIPALS_STALE_TIME });
  const { groups, isLoadingGroups, isGroupsError } = usePermissionGroups(enabled);

  const users = useMemo(() => toPrincipals(usersQuery.data), [usersQuery.data]);

  return {
    users,
    groups,
    isLoading: usersQuery.isLoading || isLoadingGroups,
    isError: usersQuery.isError || isGroupsError,
  };
};
